/**
 * Electron Shell API
 *
 * OS UI operations that intentionally go through the Electron preload.
 */

import { getExternalUrlToOpen } from "../utils/externalLinks"

export interface SelectDirectoryResponse {
    path: string | null
}

/**
 * Open directory selection dialog.
 * Returns the selected directory path or null if canceled.
 */
export async function selectDirectory(defaultPath?: string): Promise<string | null> {
    if (!window.wildlarkAPI) {
        console.warn("[ShellAPI] Not running in Electron")
        return null
    }

    const response = (await window.wildlarkAPI.shell.selectDirectory({ defaultPath })) as SelectDirectoryResponse
    return response.path
}

/**
 * Open a path in the native default app.
 */
export function openPathInNativeApp(path: string): void {
    if (!window.wildlarkAPI) {
        console.warn("[ShellAPI] Not running in Electron")
        return
    }
    window.wildlarkAPI.shell.openPath({ path })
}

export const openPathInFileManager = openPathInNativeApp

export function revealPathInFileManager(path: string, fallbackDirectoryPath: string): void {
    if (!window.wildlarkAPI) {
        console.warn("[ShellAPI] Not running in Electron")
        return
    }
    if (window.wildlarkAPI.shell.revealPath) {
        window.wildlarkAPI.shell.revealPath({ path })
        return
    }
    window.wildlarkAPI.shell.openPath({ path: fallbackDirectoryPath })
}

/**
 * Open URL in native browser.
 */
export function openUrlInNativeBrowser(url: string): void {
    const externalUrl = getExternalUrlToOpen(url)
    if (!externalUrl) {
        console.warn("[ShellAPI] Refusing to open non-external URL", { url })
        return
    }

    if (!window.wildlarkAPI) {
        window.open(externalUrl, "_blank", "noopener,noreferrer")
        return
    }

    window.wildlarkAPI.shell.openUrl({ url: externalUrl })
}
